// src/components/Dashboard/DashboardHeaderBar.tsx
//
// Shared dark header bar for the Outsourcing dashboards (EE-238): uppercase
// title on the left, search + primary action on the right.
//
// Both dashboards already drew this bar with the same inline styles; the
// Inquiry page used a raw <button> and the Requisition page a MUI Button
// hardcoded to #E0A900. global.css defines two yellows - --mainYellow
// (#e1a900) and --buttonYellow (#e0a900) - so that was a real token, just a
// different one from the Inquiry side's. Both now use --mainYellow, and the
// requisition dialogs were moved onto it too so the shade doesn't shift when
// you open one. Rendering both bars from here keeps them from drifting again.
import { FunctionComponent, ReactNode } from "react";
import { Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchField from "components/SearchField";

// The primary action is one optional object rather than three independent
// optional props (EE-238): `actionLabel` and `onAction` used to be separately
// optional and gated on both being present, so passing one without the other
// silently rendered no button at all.
type DashboardAction = {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
};

// Search is grouped for the same reason, and is optional as a whole: the
// Partner dashboard has no search at all, and a page that wants one must
// supply both halves or neither.
type DashboardSearch = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

type PropsType = {
  title: string;
  search?: DashboardSearch;
  action?: DashboardAction;
};

const DashboardHeaderBar: FunctionComponent<PropsType> = ({
  title,
  search,
  action,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      paddingRight: "10px",
      justifyContent: "space-between",
      // Content-driven height, not `6vh` (EE-238): on a short viewport 6vh
      // resolved to less than the h6 line box plus its top padding, and the
      // title spilled out of the bar. Centring the row also removes the
      // `paddingTop` that was standing in for vertical alignment.
      alignItems: "center",
      height: "56px",
      background: "var(--header)",
      color: "var(--white)",
    }}
  >
    <Typography
      style={{
        textTransform: "uppercase",
        color: "var(--white)",
        // Same 1.5rem gutter the tabs, filter bar and table below use, so the
        // page has one left edge instead of two. Replaces `1rem` plus a
        // leading `&nbsp;` (EE-238).
        paddingLeft: "1.5rem",
      }}
      fontWeight="bold"
      variant="h6"
    >
      {title}
    </Typography>
    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      {search && (
        <SearchField
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
        />
      )}
      {action && (
        <Button
          variant="contained"
          startIcon={action.icon ?? <AddIcon />}
          sx={{
            maxHeight: 40,
            background: "var(--mainYellow)",
            "&:hover": {
              color: "var(--mainYellow)",
              border: "1px solid var(--mainYellow)",
              background: "transparent",
            },
          }}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  </div>
);

export default DashboardHeaderBar;
